<script setup>
import {useModalStore} from "../../store/modal";
import {useAuthStore} from "../../store/auth";
import {onMounted, ref, watch} from "vue";
import {useSettingsStore} from "../../store/settings";

const authStore = useAuthStore()
const modalStore = useModalStore()
const settingsStore = useSettingsStore()
const theme_toggle = ref(authStore.theme === 'dark')
const work_status = ref(false)
const presales_allowed = ref(false)

watch(theme_toggle, () => {
    authStore.updateTheme(theme_toggle.value ? 'dark' : 'light');
})

watch(work_status, async () => {
    if (authStore.isAdmin()) {
        await settingsStore.toggleWorkStatus(work_status.value)
        work_status.value = JSON.parse(settingsStore.work_status?.value)
    }
})

watch(presales_allowed, async () => {
    if (authStore.isAdmin()) {
        await settingsStore.togglePresalesAllowed(presales_allowed.value)
        presales_allowed.value = JSON.parse(settingsStore.presales_allowed?.value)
    }
})

onMounted(async () => {
    if (authStore.isAdmin()) {
        await settingsStore.getSettings()
        work_status.value = JSON.parse(settingsStore.work_status?.value)
        presales_allowed.value = JSON.parse(settingsStore.presales_allowed?.value)
    }
})
</script>

<template>
    <div class="menu-modal">
        <brand-nav-button variant="icon" icon="cross-default" @click="modalStore.close()"/>

        <div class="menu-profile-block">
            <div class="menu-profile-image">
            <span>
                {{ authStore.user?.first_name[0] + authStore.user?.last_name[0] }}
            </span>
            </div>
            <div class="menu-profile-details">
                <p class="menu-profile-name">{{ authStore.user?.first_name + " " + authStore.user?.last_name }}</p>
                <p class="menu-profile-email">{{ authStore.user?.email }}</p>
                <brand-button as="button" variant="icon" @click="modalStore.open('Logout', {}, null, 'bottom-right')">
                    Log out
                    <ah-icon name="arrow-logout"/>
                </brand-button>
            </div>
        </div>

        <div class="menu-settings-block">
            <div class="menu-settings-theme-details">
                <p>Theme.</p>
                <p>{{ theme_toggle ? "Dark" : "Light" }} mode.</p>
            </div>

            <label :class="['theme-toggle', {'--is-active':theme_toggle}]">
                <input type="checkbox" name="" id="" v-model="theme_toggle">
                <span class="theme-toggle-slider"></span>
                <span class="icon-block">
            <ah-icon name="weather-sun" :size="36"/>
          </span>
                <span class="icon-block">
            <ah-icon name="weather-moon" :size="36"/>
          </span>
            </label>
        </div>

        <div class="menu-settings-block">
            <div class="menu-settings-theme-details">
                <p>Working status.</p>
                <p>{{ work_status ? "Online" : "Offline" }}.</p>
            </div>

            <label :class="['theme-toggle', {'--is-active':work_status}]">
                <input type="checkbox" name="" id="" v-model="work_status">
                <span class="theme-toggle-slider"></span>
                <span class="icon-block">
                    <ah-icon name="wifi-disabled" :size="36"/>
                </span>
                <span class="icon-block">
                    <ah-icon name="wifi-default" :size="36"/>
                </span>
            </label>
        </div>

        <div class="menu-settings-block" v-if="authStore.isAdmin()">
            <div class="menu-settings-theme-details">
                <p>Allow presales.</p>
                <p>{{ presales_allowed ? "Enabled" : "Disabled" }}.</p>
            </div>

            <label :class="['theme-toggle', {'--is-active':presales_allowed}]">
                <input type="checkbox" name="" id="" v-model="presales_allowed">
                <span class="theme-toggle-slider"></span>
                <span class="icon-block">
                    <ah-icon name="cross-default" :size="36"/>
                </span>
                <span class="icon-block">
                    <ah-icon name="check-single" :size="36"/>
                </span>
            </label>
        </div>
    </div>
</template>

<style scoped lang="scss">
.menu-modal {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    align-items: flex-end;
    width: 35.5rem;
    margin: 1rem 4rem 0;

    .nav-button {
        background: var(--color-red-raspberry);

        .icon {
            fill: var(--color-white) !important;
        }
    }

    .menu-profile-block {
        padding: 2rem 4rem 2rem 2rem;
        border-radius: 4rem;
        background: var(--color-white);
        width: 100%;
        display: flex;
        gap: 2rem;
        align-items: center;

        .menu-profile-image {
            width: 12rem;
            height: 12rem;
            border-radius: 50%;
            background: var(--color-grey-mist);
            display: flex;
            align-items: center;
            justify-content: center;

            span {
                @include font(3, 3, black);
            }
        }

        .menu-profile-details {
            display: inline-flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;

            .menu-profile-name {
                @include font(1.6, 1.6, black);
            }

            .menu-profile-email {
                @include font(1.4, 1.4, book);
                margin-bottom: 1rem;
            }

            .brand-button {
                display: flex;
                align-items: center;
                gap: 1rem;
                padding: .8rem 2rem;

                .icon {
                    fill: var(--color-white);
                }
            }
        }
    }

    .menu-settings-block {
        padding: 1rem 1rem 1rem 3rem;
        width: 100%;
        border-radius: 4rem;
        background: var(--color-white);
        display: flex;
        justify-content: space-between;
        align-items: center;

        .menu-settings-theme-details {
            display: flex;
            flex-direction: column;
            gap: .6rem;

            p {
                @include font(1.4, 1.4, black);

                &:last-of-type {
                    @include font(1.2, 1.2, black);
                    color: #768692;
                }
            }
        }
    }

    .theme-toggle {
        width: 14rem;
        height: 8rem;
        background: var(--color-grey-dew);
        border-radius: 4rem;
        display: flex;
        align-items: center;
        padding: 1rem;
        position: relative;

        input {
            display: none;
        }

        .icon-block {
            padding: 1rem;
            z-index: 1;

            .icon {
                display: block !important;
                width: 4rem !important;
                height: 4rem !important;
                fill: var(--color-white);
            }
        }

        .theme-toggle-slider {
            width: 6rem;
            height: 6rem;
            background: var(--color-red-raspberry);
            border-radius: 50%;
            display: block;
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            left: 1rem;
            transition: .3s ease-in-out;
        }

        &.--is-active {
            .theme-toggle-slider {
                left: calc(100% - 7rem);
            }
        }
    }

    @include dark() {
        .menu-profile-block {
            background: var(--color-darth-maul);

            p {
                color: var(--color-white) !important;
            }
        }

        .menu-settings-block {
            background: var(--color-darth-maul);

            .menu-settings-theme-details {
                p:first-child {
                    color: var(--color-white);
                }
            }

            .theme-toggle {
                background: var(--color-grey-thunder);
            }
        }
    }

    @include mobile() {
        margin: 1rem 1rem 0;
        width: calc(100% - 2rem);
    }
}
</style>
